<script setup lang="ts" generic="T extends SelectInputType">
import type Menu from 'primevue/menu';
import type { MenuPassThroughOptions } from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, nextTick, ref } from 'vue';

import type { SelectInputType } from '@modules/forms/types';

import type {
  VDropdownMenuEmits,
  VDropdownMenuProps,
} from './types';

const props = withDefaults(defineProps<VDropdownMenuProps<T>>(), {
  leftIconColor: 'text-primary',
});
const emit = defineEmits<VDropdownMenuEmits<T>>();

const menuRef = ref<InstanceType<typeof Menu>>();
const menuOpened = ref(false);
const id = computed<string>(() => `${props.name}-${Math.floor(Math.random() * 10000)}`);

const buttonLabel = computed<string>(() => {
  const foundItem = props.options.find((item) => item.value === props.value);
  return foundItem?.label || props.options[0]?.label || props.name;
});

const menuPtOptions: MenuPassThroughOptions = {
  list: {
    class: [
      'max-h-[434px]',
      'overflow-y-auto',
    ],
  },
};

function toggleMenu(event: Event): void {
  if (menuOpened.value) {
    menuRef.value?.hide();
  } else {
    menuRef.value?.show(event);
  }
  menuOpened.value = !menuOpened.value;
}

function hideMenu(): void {
  menuOpened.value = false;
}

function onSelect(item: MenuItem): void {
  emit('change', item.value);
  menuRef.value?.hide();
  hideMenu();
}

async function onShow(): Promise<void> {
  await nextTick();

  const el = document.querySelector('.p-menu-item-selected');

  if (el) {
    el.scrollIntoView({ block: 'nearest' });
  }
}
</script>

<template>
  <div v-data-qa-auto-id="'VDropdownMenu'"
    data-id="dropdown-menu-root"
    class="min-w-[80px]"
    :class="{
      'border-gray-200': underlined,
      'border-b-2': underlined,
    }"
  >
    <PButton
      :aria-controls="id"
      aria-haspopup="true"
      text
      :class="{ 'min-w-0': zeroMinWidth, 'hover:opacity-80': !disabled }"
      class="w-full px-3 text-content-color hover:bg-transparent"
      :disabled="disabled"
      @click="toggleMenu"
    >
      <span
        data-id="dropdown-menu-button-label"
        class="flex w-full items-center"
        :class="{
          'text-color': styleType === 'dark',
          'text-content-color': styleType === 'light',
        }"
      >
        <i
          v-if="leftIcon"
          data-id="dropdown-menu-left-icon"
          class="pi mr-2"
          :class="{ [leftIcon]: leftIcon, [leftIconColor]: !disabled }"
        />
        <span
          data-id="dropdown-menu-label"
          class="w-full text-left"
          :class="{ 'text-primary': !!label && !disabled }"
        >
          {{ label || buttonLabel }}
        </span>
        <i
          v-if="!hideChevron"
          data-id="dropdown-menu-chevron-icon"
          :class="{
            'pi-angle-down': !menuOpened,
            'pi-angle-up': menuOpened,
          }"
          class="pi ml-2"
        />
      </span>
    </PButton>
    <PMenu
      :id="id"
      ref="menuRef"
      :model="options"
      popup
      :pt="menuPtOptions"
      @show="onShow"
      @hide="hideMenu"
    >
      <template #item="{ item }">
        <span
          class="p-menu-item-link"
          :class="{
            'bg-surface-300': item.value === value,
            'p-menu-item-selected': item.value === value,
          }"
          @click="onSelect(item)"
        >
          <span class="p-menu-item-text">
            {{ item.label }}
          </span>
        </span>
      </template>
    </PMenu>
  </div>
</template>
